import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EditOrderInstructionsFlowTypes, EditOrderService } from '../../../services/edit-order.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MailboxIconComponent } from './icons/mailbox-icon/mailbox-icon.component';
import { DoorIconComponent } from './icons/door-icon/door-icon.component';
import { OtherIconComponent } from './icons/other-icon/other-icon.component';
import { IntlPhoneNumberComponent } from '../../../intl-phone-number/intl-phone-number.component';
import { TrackingInfo } from '@evermile/customer-tracking';

enum SafePlaceTypes {
  Mailbox = 'mailbox',
  Concierge = 'concierge',
  Door = 'door',
  Other = 'other',
}

interface SafePlaceOption {
  type: SafePlaceTypes;
  icon: any;
  title: string;
  label2: string;
  content2Placeholder: string;
}

const textForTypesMap = {
  [EditOrderInstructionsFlowTypes.CourierNote]: 'Leave a note for the courier',
  [EditOrderInstructionsFlowTypes.Neighbour]: 'Leave with a neighbour',
  [EditOrderInstructionsFlowTypes.SafePlace]: 'Deliver to a safe place',
};
@Component({
  selector: 'common-edit-order-instructions-form',
  templateUrl: './edit-order-instructions-form.component.html',
  styleUrls: ['./edit-order-instructions-form.component.scss'],
})
export class EditOrderInstructionsFormComponent implements OnInit, OnDestroy {
  @Input() brandColor?: string;
  @Input() order!: TrackingInfo;
  @Input() textColor?: string;
  type?: EditOrderInstructionsFlowTypes;
  form!: FormGroup;
  loading = false;
  phoneError = '';
  displayErrors = false;

  //Subscriptions
  typeSubscription?: Subscription;

  safePlaceOptions: SafePlaceOption[] = [
    {
      type: SafePlaceTypes.Mailbox,
      icon: MailboxIconComponent,
      title: 'Leave in the postbox',
      label2: 'Instructions',
      content2Placeholder: 'Add any specific request',
    },
    {
      type: SafePlaceTypes.Concierge,
      icon: MailboxIconComponent,
      title: 'Leave with the concierge',
      label2: 'Instructions',
      content2Placeholder: 'Add any specific request',
    },
    {
      type: SafePlaceTypes.Door,
      icon: DoorIconComponent,
      title: 'Leave in front of my door',
      label2: 'Instructions',
      content2Placeholder: 'Add any specific request',
    },
    {
      type: SafePlaceTypes.Other,
      icon: OtherIconComponent,
      title: 'Another place...',
      label2: 'Tell us where you want us to leave it',
      content2Placeholder: 'e.g. Leave it in the mail room at the ground floor of the building',
    },
  ];

  protected readonly EditOrderInstructionsFlowTypes = EditOrderInstructionsFlowTypes;

  //Subscriptions
  formSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private editOrderService: EditOrderService,
  ) {}

  ngOnInit() {
    this.typeSubscription = this.editOrderService.emitInstructionsOption$.subscribe((type) => {
      this.type = type;
      this.setForm();
    });
  }

  ngOnDestroy() {
    this.formSub?.unsubscribe();
  }

  onSelectSafePlace(option: SafePlaceOption) {
    if (option.type === SafePlaceTypes.Other) {
      this.form = this.fb.group({
        content1: new FormControl(option.type, Validators.required),
        content2: new FormControl('', Validators.required),
      });
    } else {
      this.form = this.fb.group({
        content1: new FormControl(option.type, Validators.required),
        content2: new FormControl(''),
      });
    }

    this.form.markAsDirty();
  }

  submitChanges() {
    this.loading = true;
    this.validateForm();
    if (this.form.invalid) {
      this.displayErrors = true;
      this.loading = false;
      return;
    }
    this.editOrderService.submitEditOrder({
      dropoffContactDetails: {
        contactName: this.order.customerName,
        contactPhone: this.order.customerPhone,
        contactEmail: this.order.customerEmail || undefined,
        instructions: this.order.instructions,
        customerInstructions: textForTypesMap[this.type!] + ': ' + this.getContent1() + ' ' + this.getContent2(),
      },
    });
  }

  validateForm() {
    if (
      this.type === EditOrderInstructionsFlowTypes.Neighbour &&
      !this.form?.value?.content2?.numberWithoutPrefixForDisplay
    ) {
      this.form.get('content2')?.get('phone')?.disable();
      this.form.get('content2')?.get('numberWithoutPrefixForDisplay')?.disable();
    } else if (this.type === EditOrderInstructionsFlowTypes.Neighbour && this.form?.get('content2')?.invalid) {
      this.phoneError = 'Invalid phone number';
    }
  }

  getContent1() {
    if (this.type === EditOrderInstructionsFlowTypes.SafePlace) {
      return this.form?.get('content1')?.value === SafePlaceTypes.Other
        ? ''
        : this.safePlaceOptions.find((o) => o.type === this.form?.get('content1')?.value)?.title || '';
    } else {
      return this.form?.get('content1')?.value || '';
    }
  }

  getContent2() {
    if (this.type === EditOrderInstructionsFlowTypes.Neighbour) {
      return this.form?.get('content2')?.value?.phone || '';
    } else {
      return this.form?.get('content2')?.value || '';
    }
  }

  setForm() {
    this.form = this.fb.group({
      content1: new FormControl('', Validators.required),
      content2:
        this.type === EditOrderInstructionsFlowTypes.Neighbour
          ? new FormGroup({
              phone: new FormControl(''),
            })
          : new FormControl(''),
    });
    this.formSub?.unsubscribe();
    this.formSub = this.form.valueChanges.subscribe((data) => {
      this.form.markAllAsTouched();
      this.phoneError = '';
      this.displayErrors = false;
    });
  }

  protected readonly SafePlaceTypes = SafePlaceTypes;
}
